import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, FileDown, ArrowUpRight } from "lucide-react";
import { FAQS } from "../data/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FAQ = () => {
    const [open, setOpen] = useState(0);
    return (
        <section id="faq" className="relative py-24 lg:py-32" data-testid="faq">
            <div className="max-w-5xl mx-auto px-5 lg:px-10">
                <div className="text-center mb-14">
                    <div className="inline-flex pill"><span className="accent-dot" />Frequently asked</div>
                    <h2 className="mt-5 font-display text-5xl lg:text-6xl text-ivory tracking-tight">
                        Questions, <span className="italic text-gold-shimmer">answered.</span>
                    </h2>
                </div>

                {/* CSR briefing PDF download - for CSR heads / legal / finance / board */}
                <motion.a
                    href={`${BACKEND_URL}/api/csr-briefing.pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="group relative block mb-8 rounded-2xl overflow-hidden"
                    style={{
                        background:
                            "linear-gradient(135deg, rgba(212,168,87,0.10) 0%, rgba(20,20,26,0.7) 60%, rgba(15,15,20,0.9) 100%)",
                        border: "1px solid rgba(212,168,87,0.28)",
                        boxShadow: "0 20px 50px -30px rgba(212,168,87,0.25)",
                    }}
                    data-testid="csr-briefing-download"
                >
                    <div className="relative p-5 lg:p-6 flex items-center gap-5">
                        <div
                            className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0 relative overflow-hidden"
                            style={{
                                background: "linear-gradient(180deg, #1A1A22, #0A0A0C)",
                                border: "1px solid rgba(212,168,87,0.4)",
                            }}
                        >
                            <FileDown className="w-6 h-6 text-gold relative z-10" />
                            <div
                                className="absolute inset-0 opacity-50 pointer-events-none"
                                style={{
                                    background:
                                        "radial-gradient(circle at 50% 50%, rgba(212,168,87,0.35), transparent 70%)",
                                }}
                            />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-mono text-[10px] uppercase tracking-widest text-gold/85 mb-1">
                                For CSR heads · Finance · Board review
                            </div>
                            <div className="font-display text-lg lg:text-xl text-ivory leading-tight">
                                Download the <span className="italic text-gold-shimmer">CSR briefing</span> (PDF, 1 page)
                            </div>
                            <div className="mt-1.5 text-[12px] text-ivory/55 leading-snug">
                                Tripartite structure · Section 135 mapping · audit-ready · engagement tiers. Forward internally to your finance &amp; legal teams.
                            </div>
                        </div>
                        <div className="hidden sm:flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-gold shrink-0 group-hover:gap-2.5 transition-all">
                            Download
                            <ArrowUpRight className="w-4 h-4" />
                        </div>
                    </div>
                </motion.a>

                <div className="space-y-3" itemScope itemType="https://schema.org/FAQPage">
                    {FAQS.map((f, i) => {
                        const isOpen = open === i;
                        return (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: i * 0.04 }}
                                className={`glass rounded-2xl overflow-hidden ${isOpen ? "border-gold/40" : ""}`}
                                itemScope
                                itemProp="mainEntity"
                                itemType="https://schema.org/Question"
                                data-testid={`faq-item-${i}`}
                            >
                                <button
                                    type="button"
                                    onClick={() => setOpen(isOpen ? -1 : i)}
                                    className="w-full px-6 lg:px-8 py-5 lg:py-6 flex items-start justify-between gap-6 text-left group"
                                    aria-expanded={isOpen}
                                    data-testid={`faq-toggle-${i}`}
                                >
                                    <span
                                        className={`font-display text-lg lg:text-xl leading-snug transition-colors ${isOpen ? "text-gold" : "text-ivory group-hover:text-gold/90"}`}
                                        itemProp="name"
                                    >
                                        {f.q}
                                    </span>
                                    <span
                                        className={`shrink-0 w-9 h-9 rounded-full border flex items-center justify-center transition-all ${isOpen ? "bg-gold border-gold text-obsidian rotate-180" : "border-white/15 text-ivory/60 group-hover:border-gold/50 group-hover:text-gold"}`}
                                    >
                                        {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                                    </span>
                                </button>
                                <AnimatePresence initial={false}>
                                    {isOpen && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                            className="overflow-hidden"
                                            itemScope
                                            itemProp="acceptedAnswer"
                                            itemType="https://schema.org/Answer"
                                        >
                                            <div
                                                className="px-6 lg:px-8 pb-6 lg:pb-7 text-ivory/70 leading-relaxed"
                                                itemProp="text"
                                            >
                                                {f.a}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default FAQ;
